import { courierQuoteOf, parseMoneyToCents, servedOutline, type LatLng } from '@agos/shared'

import { feeCentsFor, type DeliveryZone } from './delivery'

/**
 * *Oblasti doručenia* on the customer website - FRONTEND.md §9.799, owner O148.
 *
 * The rules behind the page, kept beside the checkout's own zone arithmetic because every answer here has to be the
 * checkout's: which zone a spot is in is `zoneFor`, and what a zone charges is `feeCentsFor`. A page that worked its
 * prices out on its own would one day state a fee the checkout does not charge.
 */

/** The zones a customer is shown: switched on, with a shape to draw, by name. */
export function shownZones(zones: readonly DeliveryZone[]): DeliveryZone[] {
  return zones
    .filter((zone) => zone.enabled && zoneOutline(zone).length >= 3)
    .sort((left, right) => left.name.localeCompare(right.name, 'sk'))
}

/**
 * The area a zone serves, corner by corner. A circle is the server's flat-degree circle, so what is drawn is what
 * `zoneFor` counts as inside; a polygon is its own corners. Nothing for a zone with neither. Since §9.800 this is the
 * shared `servedOutline`, which the manager's zone editor draws with too.
 */
export function zoneOutline(zone: DeliveryZone): LatLng[] {
  return servedOutline(zone)
}

/** One line of a zone's price list: an order from `fromCents` up to (not including) `toCents` pays `feeCents`. */
export interface PriceRow {
  fromCents: number
  /** Null for *and everything above*. */
  toCents: number | null
  feeCents: number
}

/**
 * What a zone charges, by order value, from its minimum upward - §9.799.
 *
 * **Read off `feeCentsFor` rather than off the tiers**, so the list states what the checkout charges: every tier edge
 * above the minimum starts a range, each range is priced by asking `feeCentsFor` at its start, and neighbours with
 * the same fee are one row. So a tier wholly below the minimum - which is never charged - is not listed, and a gap
 * between tiers shows the fee the checkout falls back to.
 */
export function priceRows(zone: DeliveryZone): PriceRow[] {
  const minimum = parseMoneyToCents(zone.minOrder)
  const starts = new Set<number>([minimum])
  for (const tier of zone.tiers) {
    for (const edge of [tier.orderFrom, tier.orderTo]) {
      if (edge === null) continue
      const cents = parseMoneyToCents(edge)
      if (cents > minimum) starts.add(cents)
    }
  }
  const rows: PriceRow[] = []
  for (const from of [...starts].sort((left, right) => left - right)) {
    const fee = feeCentsFor(zone, from)
    const previous = rows[rows.length - 1]
    if (previous !== undefined && previous.feeCents === fee) continue
    if (previous !== undefined) previous.toCents = from
    rows.push({ fromCents: from, toCents: null, feeCents: fee })
  }
  return rows
}

/**
 * Whether the footer and the *Ďalšie* sheet offer *Oblasti doručenia* - §9.799.
 *
 * Only when the zone read answered with a zone to show, and the restaurant is not one where external couriers deliver
 * (§9.786) - there no zone prices anything, and a page of zones would state fees nobody is charged. An unanswered
 * read offers nothing, which costs a link rather than showing a page that cannot be filled.
 */
export function deliveryAreasOffered(zones: readonly DeliveryZone[], quote: unknown): boolean {
  if (courierQuoteOf(quote)?.external === true) return false
  return shownZones(zones).length > 0
}
